import { useEffect, useState, useRef } from 'react';
import { Message } from '../types';
import { apiService } from '../services/api';

export const useMessageUpdates = (messageId?: string, userId?: string) => {
  const [message, setMessage] = useState<Message | undefined>(undefined);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!messageId && !userId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        if (messageId) {
          const messageData = await apiService.getMessageById(messageId);
          setMessage(messageData);
        } else if (userId) {
          const messagesData = await apiService.getMessages();
          setMessages(messagesData);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Set up polling for real-time updates every 30 seconds
    const interval = setInterval(fetchData, 30000);
    
    return () => clearInterval(interval);
  }, [messageId, userId]);

  return { message, messages, loading, error };
};

export const useRealtimeMessage = (messageId: string) => {
  const [message, setMessage] = useState<Message | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const setupWebSocket = (messageData: Message) => {
      // Clean up any existing polling
      if (pollingIntervalRef.current) {
        console.log('🧹 Cleaning up polling interval before switching to WebSocket');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }

      console.log('🚀 Live tracking available, setting up WebSocket stream...');
      console.log('📡 Stream info:', messageData.live_tracking);
      
      // Set up WebSocket connection
      const wsUrl = messageData.live_tracking!.websocket_url;
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected for live tracking');
        setIsStreaming(true);
        setError(null);
        
        // Send initial ping to establish connection
        try {
          ws.send(JSON.stringify({ type: 'ping' }));
        } catch (err) {
          console.error('❌ Error sending initial ping:', err);
        }
        
        // Set up periodic ping to keep connection alive (every 30 seconds)
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'ping' }));
            } catch (err) {
              console.error('❌ Error sending ping:', err);
              clearInterval(pingInterval);
            }
          } else {
            clearInterval(pingInterval);
          }
        }, 30000);
        
        // Store ping interval for cleanup
        (ws as any).pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        try {
          const update = JSON.parse(event.data);
          console.log('📍 Live tracking update received:', update);
          
          // Handle pong responses
          if (update.type === 'pong') {
            return; // Just acknowledge, no state update needed
          }
          
          // Handle initial data
          if (update.type === 'initial_data' && update.data) {
            setMessage(prev => prev ? {
              ...prev,
              progress: update.data.progress || prev.progress,
              current_location: update.data.current_location || prev.current_location,
              status: update.data.status || prev.status
            } : prev);
            return;
          }
          
          // Handle location updates
          if (update.type === 'location_update' && update.data) {
            setMessage(prev => prev ? {
              ...prev,
              progress: update.data.progress || prev.progress,
              current_location: update.data.current_location || prev.current_location,
              status: update.data.status || prev.status
            } : prev);
          }
        } catch (err) {
          console.error('❌ Error parsing WebSocket message:', err);
        }
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setError('WebSocket connection failed');
        setIsStreaming(false);
      };
      
      ws.onclose = () => {
        console.log('🔌 WebSocket connection closed');
        setIsStreaming(false);
        
        // Clean up ping interval
        if ((ws as any).pingInterval) {
          clearInterval((ws as any).pingInterval);
        }
      };
      
      wsRef.current = ws;
    };

    const fetchInitialData = async () => {
      if (!messageId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const messageData = await apiService.getMessageById(messageId);
        setMessage(messageData);
        
        // Check if message has live tracking available
        if (messageData.live_tracking && messageData.status === 'in_transit') {
          setupWebSocket(messageData);
        } else {
          console.log('📊 No live tracking available, using polling for message:', messageData.status);
          // Fall back to polling for non-transit messages
          const interval = setInterval(async () => {
            try {
              const updatedMessage = await apiService.getMessageById(messageId);
              setMessage(updatedMessage);
              
              // Check if message has transitioned to in_transit with live tracking
              if (updatedMessage.live_tracking && updatedMessage.status === 'in_transit' && !wsRef.current) {
                console.log('🔄 Message transitioned to in_transit, switching to live mode...');
                setupWebSocket(updatedMessage);
              }
            } catch (err) {
              console.error('❌ Polling update failed:', err);
            }
          }, 30000);
          
          pollingIntervalRef.current = interval;
        }
        
      } catch (err: any) {
        setError(err.message || 'Failed to fetch message');
        console.error('❌ Failed to fetch message:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
    
    // Cleanup function
    return () => {
      if (wsRef.current) {
        console.log('🧹 Cleaning up WebSocket connection');
        wsRef.current.close();
        wsRef.current = null;
        setIsStreaming(false);
      }
      if (pollingIntervalRef.current) {
        console.log('🧹 Cleaning up polling interval');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [messageId]);

  return { message, loading, error, isStreaming };
};

export const useRealtimeMessages = (userId: string) => {
  const { messages, loading, error } = useMessageUpdates(undefined, userId);
  return { messages: messages || [], loading, error };
};
